import { useEffect, useRef, useState, useCallback } from "react";

interface UseSlotReservationTimerOptions {
  durationMs?: number; // Default: 5 minutes (300000ms)
  onExpire: () => void;
  enabled: boolean;
  startTime: number | null; // Timestamp when the timer started
}

interface UseSlotReservationTimerReturn {
  timeRemaining: number; // Milliseconds remaining
  isExpired: boolean;
  formattedTime: string; // "MM:SS" format
  percentRemaining: number; // 0-100 for progress indicators
}

/**
 * Hook to manage a countdown timer for slot reservation.
 * Starts counting down from when the user selects a time slot.
 * Used to encourage users to complete booking within a time limit.
 */
export function useSlotReservationTimer({
  durationMs = 5 * 60 * 1000, // 5 minutes default
  onExpire,
  enabled,
  startTime,
}: UseSlotReservationTimerOptions): UseSlotReservationTimerReturn {
  const [timeRemaining, setTimeRemaining] = useState<number>(durationMs);
  const [isExpired, setIsExpired] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasExpiredRef = useRef(false);

  // Ref to store latest callback to avoid stale closure issues
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  // Calculate time remaining based on start time
  const calculateTimeRemaining = useCallback(() => {
    if (!startTime) return durationMs;
    const elapsed = Date.now() - startTime;
    const remaining = Math.max(0, durationMs - elapsed);
    return remaining;
  }, [startTime, durationMs]);

  // Initialize and update timer
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset expired state when timer restarts
    if (enabled && startTime) {
      hasExpiredRef.current = false;
      setIsExpired(false);
    }

    // Don't run if not enabled or no start time
    if (!enabled || !startTime) {
      setTimeRemaining(durationMs);
      return;
    }

    // Initial calculation
    const initialRemaining = calculateTimeRemaining();
    setTimeRemaining(initialRemaining);

    // Check if already expired
    if (initialRemaining <= 0 && !hasExpiredRef.current) {
      hasExpiredRef.current = true;
      setIsExpired(true);
      onExpireRef.current();
      return;
    }

    // Start interval to update every second
    intervalRef.current = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      if (remaining <= 0 && !hasExpiredRef.current) {
        hasExpiredRef.current = true;
        setIsExpired(true);
        onExpireRef.current();
        
        // Clear interval after expiry
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 1000);

    // Cleanup on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, startTime, durationMs, calculateTimeRemaining]);

  // Format time as "MM:SS"
  const formattedTime = useCallback(() => {
    const totalSeconds = Math.ceil(timeRemaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, [timeRemaining])();

  // Calculate percentage remaining (0-100)
  const percentRemaining = Math.round((timeRemaining / durationMs) * 100);

  return {
    timeRemaining,
    isExpired,
    formattedTime,
    percentRemaining,
  };
}

export default useSlotReservationTimer;
